import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Check, FileText, Loader2, PauseCircle, Timer } from 'lucide-react';
import { api } from '../../services/api';
import { OPEN_TICKET_STATUS_PARAM } from '../../utils/ticketStatus';
import { appLocale } from '../../utils/format';
import Field from '../ui/Field';
import StateBlock from '../ui/StateBlock';
import { formatClock } from '../home/homeModel';
import { useMyRecentTasks } from '../timer/useMyRecentTasks';
import type { Ticket, WorkLogSuggestion, WorkLogSuggestionKind } from '../../types';
import {
  MIN_SUMMARY_LENGTH, describeSuggestion, durationChoices, durationLabel, hasDurationChoice, needsTaskPick, suggestionMeta, suggestionTitle,
  type SuggestionDraft,
} from './documentationModel';
import WorkLogOverlapNotice from '../worklogs/WorkLogOverlapNotice';
import { shortenedMinutes } from '../worklogs/overlapModel';
import type { SuggestionRowError, SuggestionsController } from './useWorkLogSuggestions';
import EntityPicker from '../ui/EntityPicker';

const KIND_ICON: Record<WorkLogSuggestionKind, typeof FileText> = {
  ticket_activity: FileText,
  meeting: CalendarDays,
  suspended_timer: PauseCircle,
  open_record: Timer,
};

interface SuggestionsListProps {
  controller: SuggestionsController;
  /** "אישור הכול" at the top (off in "סגירת יום", whose primary does the same). */
  showAcceptAll?: boolean;
  /** Shown when nothing is left to document. */
  empty?: ReactNode;
  className?: string;
}

/**
 * "תיעוד מוצע" (documentation brief, part 2, 2026-10-12): what the employee
 * did today without a work log, ready to confirm. One list for the Home
 * "ממתין לך" tile and the "סגירת יום" dialog.
 *
 * Each row: the ticket (or meeting) name, customer · project, what happened
 * and when, duration buttons (15 דק׳ · 30 דק׳ · 1 שע׳ · 2 שע׳ and the
 * suggested value), the pre-filled summary, "אישור" (primary) and "לא לתעד"
 * (quiet). A meeting without a ticket asks for a task first. A row that fails
 * stays with the server's message (as the work-log form shows it); a row that
 * goes hands focus to the next one.
 */
export default function SuggestionsList({ controller, showAcceptAll = true, empty, className = '' }: SuggestionsListProps) {
  const { t } = useTranslation();
  const { query, rows } = controller;
  const listRef = useRef<HTMLUListElement>(null);
  // Focus after a row leaves: the row now at the same place, else the one before, else the list.
  const refocusAt = useRef<number | null>(null);
  const rowKeys = rows.map((r) => r.key).join('|');

  useEffect(() => {
    const at = refocusAt.current;
    if (at === null) return;
    refocusAt.current = null;
    const items = listRef.current?.querySelectorAll<HTMLElement>('li[data-suggestion-key]');
    const target = items && items.length > 0 ? items[Math.min(at, items.length - 1)] : null;
    const focusable = target?.querySelector<HTMLElement>('[data-row-focus]');
    (focusable ?? listRef.current ?? null)?.focus({ preventScroll: false });
  }, [rowKeys]);

  if (query.isLoading) return <StateBlock kind="loading" rows={2} className={className} />;
  if (query.isError) {
    return (
      <StateBlock
        kind="error"
        compact
        error={query.error}
        title={t('documentation.loadFailed')}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
        className={className}
      />
    );
  }
  if (rows.length === 0) {
    return <p className={`py-3 text-sm text-text-2 ${className}`}>{empty ?? t('documentation.empty')}</p>;
  }

  const leaving = (index: number, focusWasInside: boolean) => {
    if (focusWasInside) refocusAt.current = index;
  };

  return (
    <div className={className}>
      {showAcceptAll && rows.length > 1 && (
        <div className="flex items-center justify-between gap-3 pb-2">
          <p className="min-w-0 text-xs text-text-2">{t('documentation.listHint')}</p>
          <button
            type="button"
            onClick={() => void controller.acceptAll()}
            disabled={controller.bulkBusy}
            aria-busy={controller.bulkBusy || undefined}
            className="btn btn-secondary btn-sm flex-shrink-0"
            data-suggestions-accept-all=""
          >
            {controller.bulkBusy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
            {t('documentation.acceptAll')}
          </button>
        </div>
      )}
      <ul ref={listRef} tabIndex={-1} aria-label={t('documentation.listLabel')} className="divide-y divide-border/60 focus:outline-none">
        {rows.map((s, index) => (
          <SuggestionRow
            key={s.key}
            suggestion={s}
            draft={controller.draftOf(s)}
            error={controller.errors[s.key] ?? null}
            busy={controller.busy[s.key] ?? null}
            locked={controller.bulkBusy}
            onChange={(patch) => controller.setDraft(s.key, patch)}
            onAccept={async (inside, override) => { const ok = await controller.accept(s, override); if (ok) leaving(index, inside); }}
            onDismiss={async (inside) => { const ok = await controller.dismiss(s); if (ok) leaving(index, inside); }}
          />
        ))}
      </ul>
    </div>
  );
}

interface RowProps {
  suggestion: WorkLogSuggestion;
  draft: SuggestionDraft;
  error: SuggestionRowError | null;
  busy: 'accept' | 'dismiss' | null;
  locked: boolean;
  onChange: (patch: Partial<SuggestionDraft>) => void;
  /** D-291: `override` = the shortened minutes after an overlap. */
  onAccept: (focusWasInside: boolean, override?: Partial<SuggestionDraft>) => void;
  onDismiss: (focusWasInside: boolean) => void;
}

function SuggestionRow({ suggestion: s, draft, error, busy, locked, onChange, onAccept, onDismiss }: RowProps) {
  const { t, i18n } = useTranslation();
  const uid = useId().replace(/:/g, '');
  const rowRef = useRef<HTMLLIElement>(null);
  const locale = appLocale(i18n.language);
  const Icon = KIND_ICON[s.kind] ?? FileText;

  const fmtDuration = (m: number) => {
    const l = durationLabel(m);
    if (l.key === 'minutes') return t('workLogs.durationField.presetMinutes', { value: l.value });
    if (l.key === 'hours') return t('workLogs.durationField.presetHours', { value: l.value });
    return t('documentation.durationHm', { value: l.value });
  };
  const title = suggestionTitle(s) ?? t(s.kind === 'meeting' ? 'documentation.untitledMeeting' : 'documentation.untitled');
  const meta = suggestionMeta(s, t);
  const line = describeSuggestion(s, (iso) => formatClock(iso, locale), fmtDuration);
  // The time range is an LTR island (an isolate), so "09:12–09:48" never reads backwards in Hebrew.
  const params = {
    ...line.params,
    what: line.params.what || t('documentation.activityFallback'),
    ...(line.params.time ? { time: `⁦${line.params.time}⁩` } : {}),
  };
  const lineText = line.key === 'activityMore'
    ? t('documentation.line.activityMore', params)
    : t(`documentation.line.${line.key}`, params);
  const choices = durationChoices(s);
  const pickTask = needsTaskPick(s);
  const disabled = !!busy || locked;
  const inside = () => !!rowRef.current?.contains(document.activeElement);

  const problem = error && 'problem' in error ? error.problem : null;
  const server = error && 'server' in error ? error : null;
  const summaryError = problem === 'summary' ? t('documentation.summaryTooShort', { min: MIN_SUMMARY_LENGTH }) : undefined;
  const taskError = problem === 'task' ? t('documentation.pickTaskError') : undefined;

  return (
    <li ref={rowRef} data-suggestion-key={s.key} data-suggestion-kind={s.kind} className="py-3">
      <div className="flex items-start gap-2.5">
        <Icon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-text-3" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p id={`${uid}-title`} className="truncate text-sm font-medium text-text"><bdi>{title}</bdi></p>
          {meta && <p className="truncate text-xs text-text-2"><bdi>{meta}</bdi></p>}
          <p className="mt-0.5 text-xs text-text-2 tabular-nums">{lineText}</p>
        </div>
      </div>

      <div className="mt-2 space-y-2 ps-6">
        {pickTask && (
          <SuggestionTaskPicker
            id={`${uid}-task`}
            organizationId={s.organization_id}
            organizationName={s.organization_name}
            value={draft.ticketId}
            onChange={(ticketId) => onChange({ ticketId })}
            error={taskError}
            disabled={disabled}
          />
        )}

        {choices.length > 0 && (
          <div role="group" aria-label={t('workLogs.durationField.label')} className="flex flex-wrap gap-1.5">
            {choices.map((m) => {
              const active = draft.minutes === m;
              return (
                <button
                  key={m}
                  type="button"
                  aria-pressed={active}
                  disabled={disabled}
                  onClick={() => onChange({ minutes: m })}
                  className={`btn btn-sm rounded-full tabular-nums ${active ? 'border border-accent bg-accent-soft text-accent-soft-text' : 'btn-secondary'}`}
                >
                  {fmtDuration(m)}
                </button>
              );
            })}
          </div>
        )}

        <Field id={`${uid}-summary`} label={t('ticketDetail.summary')} error={summaryError}>
          <textarea
            className="input text-sm"
            rows={2}
            value={draft.summary}
            disabled={disabled}
            onChange={(e) => onChange({ summary: e.target.value })}
          />
        </Field>

        {/* D-291: an open record has no length to choose - it cannot be shortened from here. */}
        {server && (
          <WorkLogOverlapNotice
            error={server.server}
            fallback={t(server.action === 'accept' ? 'documentation.acceptFailed' : 'documentation.dismissFailed')}
            busy={disabled}
            onShorten={hasDurationChoice(s) ? (c) => { const m = shortenedMinutes(c); if (m) onAccept(inside(), { minutes: m }); } : undefined}
            shortenInWholeMinutes
            onConfirmParallel={() => onAccept(inside(), { confirmParallel: true })}
            onRetry={() => onAccept(inside())}
          />
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onDismiss(inside())}
            disabled={disabled}
            aria-label={t('documentation.dismissFor', { title })}
            className="btn btn-ghost btn-sm"
          >
            {busy === 'dismiss' && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {t('documentation.dismiss')}
          </button>
          <button
            type="button"
            onClick={() => onAccept(inside())}
            disabled={disabled}
            aria-busy={busy === 'accept' || undefined}
            aria-label={t('documentation.acceptFor', { title })}
            className="btn btn-primary btn-sm"
            data-row-focus=""
          >
            {busy === 'accept' ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Check size={14} aria-hidden="true" />}
            {t('documentation.accept')}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * The task for a meeting without a ticket: the open tasks of the meeting's
 * customer first, then "המשימות האחרונות שלי" (the same `useMyRecentTasks`
 * the work-log form and the timer dialog open on). No new picker.
 */
function SuggestionTaskPicker({ id, organizationId, organizationName, value, onChange, error, disabled }: {
  id: string;
  organizationId: string | null;
  organizationName: string | null;
  value: string;
  onChange: (ticketId: string) => void;
  error?: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const recent = useMyRecentTasks(8);
  const orgTasks = useQuery({
    queryKey: ['tickets', 'suggestion-org-tasks', organizationId],
    queryFn: () => api.getTickets({ organization_id: organizationId ?? undefined, status: OPEN_TICKET_STATUS_PARAM, limit: 20 }),
    enabled: !!organizationId,
    staleTime: 60_000,
    retry: 1,
  });
  const fromCustomer = ((orgTasks.data?.data ?? []) as Ticket[]).map((tk) => ({ id: tk.id, title: tk.title }));
  const seen = new Set(fromCustomer.map((x) => x.id));
  const mine = recent.items.filter((x) => !seen.has(x.id));
  const nothing = fromCustomer.length === 0 && mine.length === 0;

  return (
    <Field id={id} label={t('documentation.task')} required error={error} hint={nothing && !orgTasks.isLoading ? t('documentation.noTasks') : undefined}>
      <EntityPicker
        placeholder={t('documentation.pickTask')}
        options={[
          ...fromCustomer.map((x) => ({ value: x.id, label: x.title, group: organizationName ?? t('documentation.customerTasks') })),
          ...mine.map((x) => ({ value: x.id, label: x.title, group: t('workLogs.recentMine') })),
        ]}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    </Field>
  );
}
